"use client";

import { useEffect, useState } from "react";
import {
  readConsent,
  writeConsent,
  CONSENT_ALL_GRANTED,
  CONSENT_ONLY_NECESSARY,
  type ConsentState,
  CONSENT_VERSION,
} from "../lib/consent";

/**
 * Cookie-consent banner + preferences modal.
 *
 * - Toont bij eerste bezoek (geen consent in LS)
 * - "Alles accepteren" / "Alleen noodzakelijk" / "Voorkeuren"
 * - Dispatcht 'bw:consent:open' custom event om modal vanuit elders te openen
 *   (bv. footer-link of /cookies pagina)
 * - Push Consent Mode v2 update naar dataLayer
 */
interface Props {
  /** Brand-naam voor de banner-headline. Default = "Badkamer Wijzer" voor
   *  backwards compat. Zet "Zwaluw Comfortsanitair" voor Zwaluw deploy. */
  brandName?: string;
}

export default function CookieBanner({ brandName = "Badkamer Wijzer" }: Props = {}) {
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [draftAnalytics, setDraftAnalytics] = useState(false);
  const [draftMarketing, setDraftMarketing] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      // Delay banner-show tot na initial paint zodat 'ie niet meedoet aan
      // LCP-meting (Lighthouse NO_LCP issue). 1500ms is voldoende lang voor
      // hero-image om te renderen + Lighthouse om de LCP-candidate te
      // detecteren, en kort genoeg om geen UX-vertraging te zijn.
      const showTimer = window.setTimeout(() => setVisible(true), 1500);
      return () => window.clearTimeout(showTimer);
    }
    return undefined;
  }, []);

  useEffect(() => {
    const openHandler = () => {
      const cur = readConsent();
      setDraftAnalytics(cur?.analytics ?? false);
      setDraftMarketing(cur?.marketing ?? false);
      setPrefsOpen(true);
    };
    window.addEventListener("bw:consent:open", openHandler);
    return () => window.removeEventListener("bw:consent:open", openHandler);
  }, []);

  function acceptAll() {
    writeConsent(CONSENT_ALL_GRANTED);
    setVisible(false);
    setPrefsOpen(false);
  }

  function onlyNecessary() {
    writeConsent(CONSENT_ONLY_NECESSARY);
    setVisible(false);
    setPrefsOpen(false);
  }

  function savePrefs() {
    const state: ConsentState = {
      necessary: true,
      analytics: draftAnalytics,
      marketing: draftMarketing,
      decided_at: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
    writeConsent(state);
    setVisible(false);
    setPrefsOpen(false);
  }

  function openPrefs() {
    const cur = readConsent();
    setDraftAnalytics(cur?.analytics ?? true);
    setDraftMarketing(cur?.marketing ?? true);
    setPrefsOpen(true);
  }

  if (!visible && !prefsOpen) return null;

  return (
    <>
      {/* Banner onderin — alleen als nog geen keuze */}
      {visible && !prefsOpen ? (
        <div role="dialog" aria-label="Cookievoorkeuren" style={BANNER_STYLE}>
          <div style={BANNER_INNER}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <p style={BANNER_HEADING}>🍪 Cookies op {brandName}</p>
              <p style={BANNER_TEXT}>
                We gebruiken cookies om de site te laten werken (noodzakelijk), om bezoek te
                analyseren en om onze advertenties relevant te maken. Kies hieronder wat u
                toestaat.{" "}
                <a href="/cookies" style={LINK_STYLE}>
                  Meer info
                </a>
                .
              </p>
            </div>
            <div style={BANNER_BUTTONS}>
              <button type="button" onClick={onlyNecessary} style={BTN_SECONDARY}>
                Alleen noodzakelijk
              </button>
              <button type="button" onClick={openPrefs} style={BTN_SECONDARY}>
                Voorkeuren
              </button>
              <button type="button" onClick={acceptAll} style={BTN_PRIMARY}>
                Accepteren
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Preferences modal */}
      {prefsOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Cookievoorkeuren aanpassen"
          style={OVERLAY_STYLE}
        >
          <div style={MODAL_STYLE}>
            <h2 style={MODAL_TITLE}>Cookievoorkeuren</h2>
            <p style={MODAL_TEXT}>
              Stel zelf in welke cookies u wilt toestaan. U kunt uw keuze altijd later aanpassen
              via de link onderin elke pagina.
            </p>

            <div style={CATEGORY}>
              <div style={{ flex: 1 }}>
                <p style={CAT_TITLE}>Noodzakelijk</p>
                <p style={CAT_DESC}>
                  Nodig voor het goed functioneren van de site (formulieren, security, sessies).
                  Kunnen niet worden uitgezet.
                </p>
              </div>
              <span style={CAT_BADGE_FIXED}>altijd aan</span>
            </div>

            <div style={CATEGORY}>
              <div style={{ flex: 1 }}>
                <p style={CAT_TITLE}>Analyse</p>
                <p style={CAT_DESC}>
                  Bezoekstatistieken zodat we onze site kunnen verbeteren. Geen persoonlijke
                  profielen.
                </p>
              </div>
              <Toggle
                checked={draftAnalytics}
                onChange={setDraftAnalytics}
                label="Analyse-cookies"
              />
            </div>

            <div style={CATEGORY}>
              <div style={{ flex: 1 }}>
                <p style={CAT_TITLE}>Marketing</p>
                <p style={CAT_DESC}>
                  Laat ons relevante advertenties tonen op Google en Facebook. Bevat tracking
                  pixels van Meta en Google.
                </p>
              </div>
              <Toggle
                checked={draftMarketing}
                onChange={setDraftMarketing}
                label="Marketing-cookies"
              />
            </div>

            <div style={MODAL_ACTIONS}>
              <button type="button" onClick={onlyNecessary} style={BTN_TERTIARY}>
                Alles uit
              </button>
              <button type="button" onClick={savePrefs} style={BTN_SECONDARY}>
                Voorkeuren opslaan
              </button>
              <button type="button" onClick={acceptAll} style={BTN_PRIMARY}>
                Alles aan
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      style={{
        position: "relative",
        width: 44,
        height: 26,
        borderRadius: 999,
        border: "none",
        background: checked ? COLOR.cta : "#CBD1D9",
        cursor: "pointer",
        padding: 0,
        flexShrink: 0,
        transition: "background 120ms",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: checked ? 21 : 3,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "#FFFFFF",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          transition: "left 120ms",
        }}
      />
    </button>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────

const COLOR = {
  ink: "#141C28",
  inkLight: "#3A3A36",
  muted: "#6E6A60",
  cream: "#F6F2EA",
  creamDark: "#EAE4D7",
  border: "#E4DED3",
  cta: "#FF6B1A",
  ctaDark: "#E05510",
  white: "#FFFFFF",
};

const BANNER_STYLE: React.CSSProperties = {
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  background: COLOR.white,
  borderTop: `2px solid ${COLOR.cta}`,
  padding: "16px 20px",
  paddingBottom: "calc(16px + env(safe-area-inset-bottom))",
  zIndex: 9999,
  boxShadow: "0 -4px 16px rgba(20,28,40,0.12)",
  fontFamily: "system-ui, -apple-system, sans-serif",
};

const BANNER_INNER: React.CSSProperties = {
  maxWidth: 1200,
  margin: "0 auto",
  display: "flex",
  gap: 18,
  alignItems: "center",
  flexWrap: "wrap",
};

const BANNER_HEADING: React.CSSProperties = {
  fontWeight: 600,
  color: COLOR.ink,
  fontSize: 15,
  margin: "0 0 4px",
};

const BANNER_TEXT: React.CSSProperties = {
  fontSize: 13.5,
  color: COLOR.inkLight,
  margin: 0,
  lineHeight: 1.5,
};

const BANNER_BUTTONS: React.CSSProperties = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

const BTN_PRIMARY: React.CSSProperties = {
  padding: "10px 18px",
  borderRadius: 8,
  border: "none",
  background: COLOR.cta,
  color: COLOR.white,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

const BTN_SECONDARY: React.CSSProperties = {
  padding: "10px 18px",
  borderRadius: 8,
  border: `1px solid ${COLOR.ink}`,
  background: COLOR.white,
  color: COLOR.ink,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

const BTN_TERTIARY: React.CSSProperties = {
  padding: "10px 18px",
  borderRadius: 8,
  border: "none",
  background: "transparent",
  color: COLOR.muted,
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "inherit",
};

const LINK_STYLE: React.CSSProperties = {
  color: COLOR.cta,
  textDecoration: "underline",
};

const OVERLAY_STYLE: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(20,28,40,0.45)",
  display: "grid",
  placeItems: "center",
  padding: 20,
  zIndex: 10000,
  fontFamily: "system-ui, -apple-system, sans-serif",
};

const MODAL_STYLE: React.CSSProperties = {
  background: COLOR.white,
  borderRadius: 16,
  maxWidth: 520,
  width: "100%",
  padding: "28px 28px 24px",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
};

const MODAL_TITLE: React.CSSProperties = {
  fontFamily: "'Fraunces', Georgia, serif",
  fontSize: 24,
  fontWeight: 500,
  color: COLOR.ink,
  margin: "0 0 8px",
  letterSpacing: "-0.01em",
};

const MODAL_TEXT: React.CSSProperties = {
  fontSize: 14,
  color: COLOR.inkLight,
  margin: "0 0 18px",
  lineHeight: 1.55,
};

const CATEGORY: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: "14px 0",
  borderTop: `1px solid ${COLOR.border}`,
};

const CAT_TITLE: React.CSSProperties = {
  fontWeight: 600,
  color: COLOR.ink,
  margin: "0 0 3px",
  fontSize: 14,
};

const CAT_DESC: React.CSSProperties = {
  fontSize: 12.5,
  color: COLOR.muted,
  margin: 0,
  lineHeight: 1.5,
};

const CAT_BADGE_FIXED: React.CSSProperties = {
  fontSize: 11,
  color: COLOR.muted,
  fontStyle: "italic",
  flexShrink: 0,
};

const MODAL_ACTIONS: React.CSSProperties = {
  display: "flex",
  gap: 8,
  marginTop: 18,
  paddingTop: 14,
  borderTop: `1px solid ${COLOR.border}`,
  flexWrap: "wrap",
  justifyContent: "flex-end",
};
